import { css, LitElement, html } from 'lit';
import { classMap } from 'lit/directives/class-map.js';
import { ScopedElementsMixin } from '@open-wc/scoped-elements';
import AabIcon from '@aab/sc-aab-icon/dist/AabIcon';
import { sy_notification_info_outlined_16, sy_notification_info_16, sy_notification_warning_outlined_16, sy_notification_warning_16, sy_notification_verified_outlined_16, sy_notification_verified_16, sy_notification_error_outlined_16, sy_notification_error_16 } from '@aab/sc-aab-icon-set';
import { info400, warning400, positive400, negative400, n400 } from '@aab/sc-styles-colors';

var css_248z = css`*,:after,:before{box-sizing:border-box}.background,.px-1{padding-left:8px!important}.background,.pr-1,.px-1{padding-right:8px!important}.icon,.mr-1{margin-right:8px!important}.text-small{font-size:.875rem;line-height:1.25rem}.d-flex{display:flex!important}.d-block{display:block!important}.d-inline-block{display:inline-block!important}.align-items-start{-ms-flex-align:start!important;align-items:flex-start!important}.background,.rounded-2{border-radius:2px!important}.align-middle{vertical-align:middle!important}[hidden]{display:none!important}.background{padding-bottom:2px;padding-top:2px}.icon{margin-top:2px}.inactive{color:#666}.inactive.background{background-color:#f0f0f0}.info{color:#0369a1}.info.background{background-color:#e0f2fe}.negative{color:#b91c1c}.negative.background{background-color:#fee2e2}.positive{color:#4d7c0f}.positive.background{background-color:#ecfccb}.warning{color:#c2410c}.warning.background{background-color:#ffedd5}`;

/**
 * @prop {string} message The status message to in the component. If there is no message the component is hidden.
 * @prop {string} styleType The style type of the status. Possible options are: `info`, `positive`, `warning`, `negative`, `inactive`.
 * @prop {boolean} background Toggle the background.
 * @prop {boolean} icon Toggle the icon.
 * @prop {string} role Attribute `role="status"` is needed to set the role of the status component
 * @tagname aab-status
 */
class AabStatus extends ScopedElementsMixin(LitElement) {
  constructor() {
    super();
    // Initialize LitElement properties here
    this.styleType = 'info';
    this.message = '';
    this.background = false;
    this.icon = false;
    this.role = 'status';
  }

  static get properties() {
    return {
      styleType: {
        type: String,
        attribute: 'style-type',
        converter: (value) => (AabStatus.types.some((t) => t === value) ? value : 'info'),
      },
      message: { type: String, attribute: 'status-message' },
      background: { type: Boolean, attribute: 'has-background' },
      icon: { type: Boolean, attribute: 'has-icon' },
      role: { type: String, reflect: true },
    };
  }

  static get scopedElements() {
    return {
      'aab-icon': AabIcon,
    };
  }

  static get styles() {
    return [css_248z];
  }

  static get types() {
    return ['info', 'positive', 'warning', 'negative', 'inactive'];
  }

  static getIconInfo(styleType, background) {
    switch (styleType) {
      case 'inactive':
        return { svg: background ? sy_notification_verified_outlined_16 : sy_notification_verified_16, color: n400 };
      case 'negative':
        return { svg: background ? sy_notification_error_outlined_16 : sy_notification_error_16, color: negative400 };
      case 'positive':
        return { svg: background ? sy_notification_verified_outlined_16 : sy_notification_verified_16, color: positive400 };
      case 'warning':
        return { svg: background ? sy_notification_warning_outlined_16 : sy_notification_warning_16, color: warning400 };
      case 'info':
      default:
        return { svg: background ? sy_notification_info_outlined_16 : sy_notification_info_16, color: info400 };
    }
  }

  renderIcon() {
    const icon = AabStatus.getIconInfo(this.styleType, this.background);
    return html`<aab-icon class="icon d-block" svg=${icon.svg} color=${icon.color} size="2" alt="" aria-hidden="true"></aab-icon>`;
  }

  render() {
    const dynamicClasses = {
      background: this.background,
      [this.styleType]: true,
    };
    // Need d-inline-block and align-middle to make sure this component is properly aligned when used in text.
    return html`
      <div class="d-inline-block align-middle text-small ${classMap(dynamicClasses)}" ?hidden=${this.message === ''}>
        <div class="d-flex align-items-start">
          ${this.icon ? this.renderIcon() : ''}
          <span class="text">${this.message}</span>
        </div>
      </div>
    `;
  }
}

export { AabStatus as default };
